import { useState, useRef, useEffect, useCallback } from 'react';
import { isGoogleDriveConfigured } from '../../config/googleDrive';

interface DataMenuProps {
  onImportLocal: () => void;
  onExportLocal: () => void;
  onDriveSave: () => void;
  onDriveLoad: () => void;
}

export function DataMenu({
  onImportLocal,
  onExportLocal,
  onDriveSave,
  onDriveLoad,
}: DataMenuProps) {
  const [isOpen, setIsOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const driveConfigured = isGoogleDriveConfigured();

  const close = useCallback(() => setIsOpen(false), []);

  useEffect(() => {
    if (!isOpen) return;

    function handleClickOutside(e: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        close();
      }
    }

    function handleEscape(e: KeyboardEvent) {
      if (e.key === 'Escape') close();
    }

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen, close]);

  return (
    <div ref={menuRef} className="relative">
      <button
        onClick={() => setIsOpen(!isOpen)}
        aria-expanded={isOpen}
        aria-haspopup="true"
        className="px-4 py-2 bg-white text-brand-primary rounded hover:bg-light-gray transition min-h-[44px]"
      >
        Datos
      </button>

      {isOpen && (
        <div
          role="menu"
          className="absolute right-0 mt-2 w-56 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-50"
        >
          <p className="px-3 py-1 text-xs text-mid-gray uppercase tracking-wider">
            Local
          </p>
          <button
            role="menuitem"
            onClick={() => {
              onImportLocal();
              close();
            }}
            className="w-full text-left px-3 py-2 text-near-black hover:bg-gray-50 transition"
          >
            Importar datos
          </button>
          <button
            role="menuitem"
            onClick={() => {
              onExportLocal();
              close();
            }}
            className="w-full text-left px-3 py-2 text-near-black hover:bg-gray-50 transition"
          >
            Exportar datos
          </button>

          {driveConfigured && (
            <>
              <div className="border-t border-gray-200 my-1" />
              <p className="px-3 py-1 text-xs text-mid-gray uppercase tracking-wider">
                Google Drive
              </p>
              <button
                role="menuitem"
                onClick={() => {
                  onDriveSave();
                  close();
                }}
                className="w-full text-left px-3 py-2 text-near-black hover:bg-gray-50 transition"
              >
                Guardar en Drive
              </button>
              <button
                role="menuitem"
                onClick={() => {
                  onDriveLoad();
                  close();
                }}
                className="w-full text-left px-3 py-2 text-near-black hover:bg-gray-50 transition"
              >
                Cargar desde Drive
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
}
